// Pure helpers for the adjustment grid cells: input parsing + display
// formatting. Lives in its own file so unit tests can import without
// pulling React / DataAdapter context. Re-exported by AdjustmentGrid.tsx
// to keep the existing module path stable for consumers.
//
// Unit convention: adjustment cells are displayed in £k (e.g. "£40k",
// "£3k", "£500k"). Bare numeric input is interpreted in £k to match the
// display, so "40" means £40,000 — not £40. Reps frequently type the
// bare number expecting £k, and the previous "£40 means £40 literal"
// behaviour caused a real data-integrity hazard. Explicit escapes:
//   - "k" / "K" suffix:                       "40k"    → £40,000
//   - "m" / "M" suffix:                       "1.5m"   → £1,500,000
//   - "£" prefix (literal pounds escape):     "£40"    → £40

export function monthKey(year: number, month: number): string {
  return `${year}-${month}`;
}

export function fmtCellDisplay(amt: number): string {
  if (amt === 0) return "··";
  // Always display in £k for visual consistency across the forecasting
  // tables — mixed units (£535 next to £8k in the same column) make it
  // hard to compare magnitudes at a glance. Sub-£1k values round to
  // "£0k" rather than dropping to raw pounds. Negative values that
  // round to zero lose the sign (no "-£0k").
  const k = Math.round(amt / 1000);
  if (k === 0) return "£0k";
  return `${k < 0 ? "-" : ""}£${Math.abs(k)}k`;
}

export function parseCellInput(raw: string): number {
  const cleaned = raw.replace(/[£,\s]/g, "");
  if (cleaned === "" || cleaned === "-") return 0;

  const isLiteralPounds = /^-?£/.test(raw);

  const kMatch = /^(-?\d+(?:\.\d+)?)k$/i.exec(cleaned);
  if (kMatch) return Math.round(parseFloat(kMatch[1]) * 1000);

  const mMatch = /^(-?\d+(?:\.\d+)?)m$/i.exec(cleaned);
  if (mMatch) return Math.round(parseFloat(mMatch[1]) * 1_000_000);

  const n = parseFloat(cleaned);
  if (Number.isNaN(n)) return 0;
  if (isLiteralPounds) return Math.round(n);
  return Math.round(n * 1000);
}
